import { required, email, minLength, maxLength, numeric, helpers } from '@vuelidate/validators';

/**
 * Converte field.validations (do backend) em regras do Vuelidate
 * com suporte a validação em tempo real (debounced)
 */
export function buildValidationRules(fields) {
  const rules = {};

  fields.forEach(field => {
    const fieldRules = {};

    // Obrigatório
    if (field.required) {
      fieldRules.required = helpers.withMessage(
        `${field.label} é obrigatório`,
        required
      );
    }

    // Email
    if (field.type === 'email') {
      fieldRules.email = helpers.withMessage(
        'E-mail inválido',
        email
      );
      fieldRules.emailQuality = helpers.withMessage(
        'Informe um e-mail válido',
        (value) => {
          if (!value) return true
          const [local, domain] = value.split('@')
          if (!local || !domain) return false
          const cleanLocal = local.toLowerCase().replace(/[^a-z0-9]/g, '')
          const domainName = domain.split('.')[0].toLowerCase()
          if (/(.)\1{2,}/.test(cleanLocal)) return false
          if (domainName && /^(.)\1+$/.test(domainName)) return false
          if (cleanLocal === domainName && cleanLocal.length > 0) return false
          return true
        }
      );
    }

    // Validações customizadas do backend.
    // Aceita dois formatos:
    //   1) Objeto:  { min_length: 5, max_length: 100, ... }
    //   2) Array (formato emitido pelo FieldEditor do admin):
    //      [ { rule: 'max_length', chars: 100 }, { rule: 'min_age', years: 18 }, ... ]
    // Normalizamos pra objeto antes de processar.
    const validations = normalizeValidations(field.validations);
    if (validations) {
      if (validations.min_length) {
        fieldRules.minLength = helpers.withMessage(
          `Mínimo de ${validations.min_length} caracteres`,
          minLength(validations.min_length)
        );
      }

      if (validations.max_length) {
        fieldRules.maxLength = helpers.withMessage(
          `Máximo de ${validations.max_length} caracteres`,
          maxLength(validations.max_length)
        );
      }

      if (validations.numeric) {
        fieldRules.numeric = helpers.withMessage(
          'Apenas números são permitidos',
          numeric
        );
      }

      if (validations.min_value) {
        fieldRules.minValue = helpers.withMessage(
          `Valor mínimo: ${validations.min_value}`,
          (value) => !value || Number(value) >= validations.min_value
        );
      }

      if (validations.max_value) {
        fieldRules.maxValue = helpers.withMessage(
          `Valor máximo: ${validations.max_value}`,
          (value) => !value || Number(value) <= validations.max_value
        );
      }

      // Regex customizado
      if (validations.pattern) {
        const regex = new RegExp(validations.pattern);
        fieldRules.pattern = helpers.withMessage(
          validations.pattern_message || 'Formato inválido',
          (value) => !value || regex.test(value)
        );
      }
    }

    // Validação customizada por tipo de campo
    if (field.type === 'cpf') {
      fieldRules.cpfValid = helpers.withMessage(
        'CPF inválido',
        (value) => !value || validateCPF(value)
      );
    }

    if (field.type === 'cpf_cnpj' || field.type === 'document' || field.type === 'cnpj') {
      fieldRules.cpfCnpjValid = helpers.withMessage(
        'CPF/CNPJ inválido',
        (value) => {
          if (!value) return true;
          const clean = value.replace(/[^\d]/g, '');
          if (clean.length <= 11) return validateCPF(value);
          return validateCNPJ(value);
        }
      );
    }

    if (field.type === 'phone' || field.type === 'tel') {
      fieldRules.phoneValid = helpers.withMessage(
        'Telefone inválido',
        (value) => !value || validatePhone(value)
      );
    }

    if (field.type === 'cep') {
      fieldRules.cepValid = helpers.withMessage(
        'CEP inválido',
        (value) => !value || validateCEP(value)
      );
    }

    rules[field.name] = fieldRules;
  });

  return rules;
}

/**
 * Validação de CNPJ
 */
function validateCNPJ(cnpj) {
  cnpj = cnpj.replace(/[^\d]/g, '');
  if (cnpj.length !== 14) return false;
  if (/^(\d)\1+$/.test(cnpj)) return false;

  let tamanho = cnpj.length - 2;
  let numeros = cnpj.substring(0, tamanho);
  let digitos = cnpj.substring(tamanho);
  let soma = 0;
  let pos = tamanho - 7;
  for (let i = tamanho; i >= 1; i--) {
    soma += numeros.charAt(tamanho - i) * pos--;
    if (pos < 2) pos = 9;
  }
  let resultado = soma % 11 < 2 ? 0 : 11 - soma % 11;
  if (`${resultado}` !== digitos.charAt(0)) return false;

  tamanho = tamanho + 1;
  numeros = cnpj.substring(0, tamanho);
  soma = 0;
  pos = tamanho - 7;
  for (let i = tamanho; i >= 1; i--) {
    soma += numeros.charAt(tamanho - i) * pos--;
    if (pos < 2) pos = 9;
  }
  resultado = soma % 11 < 2 ? 0 : 11 - soma % 11;
  return `${resultado}` === digitos.charAt(1);
}

/**
 * Validação de CPF
 */
function validateCPF(cpf) {
  cpf = cpf.replace(/[^\d]/g, '');
  if (cpf.length !== 11) return false;
  if (/^(\d)\1+$/.test(cpf)) return false;

  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cpf.charAt(i)) * (10 - i);
  }
  let digit = 11 - (sum % 11);
  if (digit >= 10) digit = 0;
  if (digit !== parseInt(cpf.charAt(9))) return false;

  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cpf.charAt(i)) * (11 - i);
  }
  digit = 11 - (sum % 11);
  if (digit >= 10) digit = 0;
  if (digit !== parseInt(cpf.charAt(10))) return false;

  return true;
}

/**
 * Validação de telefone (formato brasileiro)
 */
function validatePhone(phone) {
  const cleaned = phone.replace(/[^\d]/g, '');
  if (cleaned.length < 10 || cleaned.length > 11) return false;
  const numberPart = cleaned.slice(2);
  if (/^(\d)\1+$/.test(numberPart)) return false;
  if ('0123456789'.includes(numberPart)) return false;
  if ('9876543210'.includes(numberPart)) return false;
  return true;
}

/**
 * Validação de CEP
 */
function validateCEP(cep) {
  const cleaned = cep.replace(/[^\d]/g, '');
  return cleaned.length === 8;
}

/**
 * Extrai o max_length de field.validations (aceita array ou objeto).
 * Retorna number ou null. Usado pra atributo HTML maxlength nos inputs.
 */
export function getFieldMaxLength(field) {
  const v = normalizeValidations(field?.validations);
  return v && v.max_length ? Number(v.max_length) : null;
}

/**
 * Converte field.validations para objeto plano com chaves esperadas pelo
 * validationBuilder. Aceita objeto (formato direto) ou array no shape
 * emitido pelo FieldEditor do admin: [{rule, chars|years|date|max_kb|...}].
 */
function normalizeValidations(validations) {
  if (!validations) return null;
  if (!Array.isArray(validations)) return validations;

  const out = {};
  for (const item of validations) {
    if (!item || !item.rule) continue;
    switch (item.rule) {
      case 'min_length':
      case 'max_length':
        if (item.chars !== undefined && item.chars !== null && item.chars !== '') {
          out[item.rule] = Number(item.chars);
        }
        break;
      case 'min_age':
      case 'max_age':
        if (item.years !== undefined) out[item.rule] = Number(item.years);
        break;
      case 'date_before':
      case 'date_after':
        if (item.date !== undefined) out[item.rule] = item.date;
        break;
      case 'file_size':
        if (item.max_kb !== undefined) out.file_size_kb = Number(item.max_kb);
        break;
      case 'file_type':
        if (item.types !== undefined) out.file_types = item.types;
        break;
      default:
        // Regras desconhecidas ainda assim viram chave (forward-compat)
        out[item.rule] = item.value !== undefined ? item.value : true;
    }
  }
  return out;
}

/**
 * Debounce helper para validação em tempo real
 */
export function debounce(fn, delay = 300) {
  let timeoutId;
  return function (...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), delay);
  };
}
